import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { num } from '../../format';
import { publishRelease, publishedRelease } from './releases';

/**
 * Which Android build the site hands out.
 *
 * Publishing used to mean copying two files onto the server over ssh, which is
 * fine right up until the person holding the build is not the person holding
 * the server. Here they are handed to the running application instead.
 *
 * Both files, always. An APK without its manifest is a build nobody is told
 * about; a manifest without its APK offers an update that 404s; and a manifest
 * describing a *different* build is the worst of the three, because nothing
 * looks wrong until somebody installs what they were not promised. The server
 * checks the digest in the manifest against the bytes that arrive, so that pair
 * cannot be published at all.
 */
/** Where nginx hands the file out. An address, not a phrase — it is the same in
 *  every language and the lint rule that keeps prose in the locale files is
 *  right to ask about anything typed straight into the markup. */
const DOWNLOAD = '/app/storageapp.apk';

export function ReleasePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const apk = useRef<HTMLInputElement>(null);
  const manifest = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const current = useQuery({ queryKey: ['app', 'release'], queryFn: publishedRelease });

  const publish = useMutation({
    mutationFn: (files: { apk: File; manifest: File }) =>
      publishRelease(files.apk, files.manifest),
    onSuccess: () => {
      setError(null);
      if (apk.current) apk.current.value = '';
      if (manifest.current) manifest.current.value = '';
      void queryClient.invalidateQueries({ queryKey: ['app', 'release'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const published = current.data ?? null;

  return (
    <div className="release">
      <h2>{t('release.title')}</h2>
      <p className="panel__hint">{t('release.intro')}</p>

      <section className="card">
        <h3>{t('release.published')}</h3>
        {current.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
        {!current.isLoading && !published && (
          <p className="panel__hint">{t('release.nothingYet')}</p>
        )}
        {published && (
          <dl className="panel__facts">
            <dt>{t('release.version')}</dt>
            <dd>{published.version}</dd>
            <dt>{t('release.size')}</dt>
            <dd>{t('release.megabytes', { n: num((published.size / 1048576).toFixed(1)) })}</dd>
            <dt>{t('release.released')}</dt>
            <dd>{published.released || '—'}</dd>
            <dt>{t('release.link')}</dt>
            <dd>
              <a href={DOWNLOAD} download>
                {DOWNLOAD}
              </a>
            </dd>
          </dl>
        )}
      </section>

      <section className="card">
        <h3>{t('release.upload')}</h3>
        <p className="panel__hint">{t('release.uploadHint')}</p>

        {error && <p className="panel__error">{t(`release.errors.${error}`, t('release.errors.unknown'))}</p>}

        <form
          className="release__form"
          onSubmit={(event) => {
            event.preventDefault();
            const chosen = apk.current?.files?.[0];
            const described = manifest.current?.files?.[0];
            if (!chosen || !described) {
              setError('both_needed');
              return;
            }
            publish.mutate({ apk: chosen, manifest: described });
          }}
        >
          <label>
            {t('release.apkFile')}
            <input ref={apk} type="file" accept=".apk,application/vnd.android.package-archive" />
          </label>
          <label>
            {t('release.manifestFile')}
            <input ref={manifest} type="file" accept=".json,application/json" />
          </label>
          <button type="submit" className="primary" disabled={publish.isPending}>
            {publish.isPending ? t('release.publishing') : t('release.publish')}
          </button>
        </form>

        {publish.isSuccess && (
          <p className="panel__hint">
            {t('release.done', { version: publish.data.version })}
          </p>
        )}
      </section>
    </div>
  );
}
